import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, OnDestroy } from '@angular/core';
import { AlertsService } from '../../services/alerts.service';
import { Router } from '@angular/router';
import { DataSharingService } from 'src/app/core/data-sharing.service';
import { Notification } from '../../models/notification.model';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-notification-bar',
  templateUrl: './notification-bar.component.html',
  styleUrls: ['./notification-bar.component.scss', './notification-shared.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class NotificationBarComponent implements OnInit, OnDestroy {

  @Input() isNotificationBarCollapsed = false;
  @Output() isNotificationBarCollapsedChange = new EventEmitter<boolean>();
  @Output() unreadCountChanged = new EventEmitter<number>();

  notificationsList: Notification[] = [];
  unreadCount = 0;
  isLoading = false;
  wsConnected = false;

  private subscriptions = new Subscription();

  closeNotificationBar() {
    this.isNotificationBarCollapsed = !this.isNotificationBarCollapsed;
    this.isNotificationBarCollapsedChange.emit(this.isNotificationBarCollapsed);
  }

  constructor(
    private alertService: AlertsService,
    private router: Router,
    private dataService: DataSharingService
  ) { }

  ngOnInit() {
    this.subscribeToNotifications();
    this.loadNotifications();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /**
   * Subscribe to notification service observables
   */
  private subscribeToNotifications() {
    // Subscribe to notifications list
    this.subscriptions.add(
      this.dataService.notifications$.subscribe((notifications: Notification[]) => {
        this.notificationsList = notifications;
      })
    );

    // Subscribe to unread count
    this.subscriptions.add(
      this.dataService.unreadCount$.subscribe((count: number) => {
        this.unreadCount = count;
        this.unreadCountChanged.emit(count);
      })
    );

    // Subscribe to WebSocket connection status
    this.subscriptions.add(
      this.dataService.wsConnected$.subscribe((connected: boolean) => {
        this.wsConnected = connected;
        console.log('WebSocket status in notification bar:', connected ? 'Connected ✅' : 'Disconnected ❌');
      })
    );
  }

  /**
   * Load notifications from API
   * TODO: Replace hardcoded oppId with actual user's opportunity ID
   */
  private loadNotifications() {
    this.isLoading = true;
    const oppId = '006a012354'; // TODO: Get from user session/storage

    this.dataService.getNotifications(true).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        console.log('Notifications loaded successfully', response);
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error('Error loading notifications', error);
      }
    });
  }

  /**
   * Called when user clicks on a notification
   */
  async openNotification(notification: Notification) {
    if (!notification.read) {
      // Mark as read via API
      this.dataService.markNotificationAsRead(notification.id).subscribe({
        next: () => {
          console.log('Notification marked as read');
        },
        error: (error: any) => {
          console.error('Error marking notification as read', error);
        }
      });
    }

    // Show notification details
    await this.alertService.presentSimpleAlert({
      header: notification.title,
      message: notification.body,
      buttonText: 'OK'
    });
  }

  /**
   * Called when user clicks "Mark all read"
   */
  markAllAsRead() {
    // TODO: Implement mark all as read functionality
    console.log('Mark all as read - functionality to be implemented');
  }

  /**
   * Get relative time string (e.g., "1h ago", "2d ago")
   */
  getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return diffMins <= 1 ? 'Just now' : `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else {
      return `${diffDays}d ago`;
    }
  }

  /**
   * Navigate to notifications detail page
   */
  viewAllNotifications() {
    this.closeNotificationBar();
    // Navigate to notifications detail page
    setTimeout(() => {
      this.router.navigate(['/notifications']);
    }, 900);
  }
}
